

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { UserIcon, CalendarIcon, TrophyIcon, ChartBarIcon, FireIcon, CheckCircleIcon } from './icons/InterfaceIcons';

// Helper component for displaying a row of information in view mode
const InfoRow = ({ label, value }) => (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-brand-text-secondary">{label}</dt>
        <dd className="mt-1 text-sm text-brand-text-primary sm:col-span-2 sm:mt-0">{value || 'N/A'}</dd>
    </div>
);

// Helper component for an input field in edit mode
const InputField = ({ label, name, value, onChange, type = 'text' }) => (
    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-4 first:pt-0">
        <label htmlFor={name} className="block text-sm font-medium text-brand-text-secondary sm:mt-px sm:pt-2">{label}</label>
        <div className="mt-1 sm:col-span-2 sm:mt-0">
            <input
                type={type}
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                className="block w-full max-w-lg rounded-md border-brand-border shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:max-w-xs sm:text-sm bg-brand-surface"
            />
        </div>
    </div>
);

// FIX: Add explicit types for props to resolve 'children' is missing error.
const SelectField: React.FC<{ label: any; name: any; value: any; onChange: any; children: React.ReactNode; }> = ({ label, name, value, onChange, children }) => (
    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-4 first:pt-0">
        <label htmlFor={name} className="block text-sm font-medium text-brand-text-secondary sm:mt-px sm:pt-2">{label}</label>
        <div className="mt-1 sm:col-span-2 sm:mt-0">
            <select
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                className="block w-full max-w-lg rounded-md border-brand-border shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:max-w-xs sm:text-sm bg-brand-surface"
            >
                {children}
            </select>
        </div>
    </div>
);

// Fix: Define an interface for the form data to resolve type errors.
interface ProfileFormData {
    fullName?: string;
    dob?: string;
    phone?: string;
    whatsapp?: string;
    address?: string;
    school?: string;
    class?: string;
    board?: string;
    medium?: string;
    exam?: string;
    targetYear?: string;
    subjectPreference?: string;
    dailyStudyGoal?: string;
}

export const ProfilePage = ({ user }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    // Fix: Type the formData state to allow accessing its properties.
    const [formData, setFormData] = useState<ProfileFormData>({});
    const [performanceProfile, setPerformanceProfile] = useState<any>(null);
    const [profileLoading, setProfileLoading] = useState(true);

     useEffect(() => {
        // Fetch performance profile
        const fetchPerformance = async () => {
            try {
                const profileRef = db.ref(`users/${user.uid}/performance_profile`);
                const snapshot = await profileRef.once('value');
                if (snapshot.exists()) {
                    setPerformanceProfile(snapshot.val());
                }
            } catch (err) {
                console.error("Failed to fetch performance profile:", err);
            } finally {
                setProfileLoading(false);
            }
        };

        fetchPerformance();

        // Initialize form data when the user prop changes
        setFormData({
            fullName: user.fullName || user.name || '',
            dob: user.dob || '',
            phone: user.phone || '',
            whatsapp: user.whatsapp || '',
            address: user.address || '',
            school: user.school || '',
            class: user.class || '',
            board: user.board || '',
            medium: user.medium || '',
            exam: user.exam || '',
            targetYear: user.targetYear || '',
            subjectPreference: user.subjectPreference || '',
            dailyStudyGoal: user.dailyStudyGoal || '',
        });
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveChanges = async () => {
        setLoading(true);
        try {
            const userRef = db.ref(`users/${user.uid}`);
            await userRef.update(formData);
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to update profile:", error);
            // Optionally: show an error toast to the user
        } finally {
            setLoading(false);
        }
    };
    
    // Renders the user's profile information in a read-only view.
    const renderViewMode = () => (
        <>
            {/* Personal Information */}
            <h3 className="text-lg font-semibold text-brand-text-primary mb-2">Personal Information</h3>
            <dl className="divide-y divide-brand-border border-t border-brand-border">
                <InfoRow label="Full Name" value={user.fullName || user.name} />
                <InfoRow label="Email Address" value={user.email} />
                <InfoRow label="Phone Number" value={user.phone} />
                <InfoRow label="WhatsApp Number" value={user.whatsapp} />
                <InfoRow label="Date of Birth" value={user.dob} />
                <InfoRow label="Address" value={user.address} />
            </dl>
             {/* Academic Information */}
            <h3 className="text-lg font-semibold text-brand-text-primary mt-6 mb-2">Academic Information</h3>
             <dl className="divide-y divide-brand-border border-t border-brand-border">
                <InfoRow label="Target Exam" value={user.exam} />
                <InfoRow label="Target Year" value={user.targetYear} />
                <InfoRow label="Class" value={user.class} />
                <InfoRow label="School" value={user.school} />
                <InfoRow label="Board" value={user.board} />
                <InfoRow label="Medium" value={user.medium} />
            </dl>
             {/* Preferences */}
            <h3 className="text-lg font-semibold text-brand-text-primary mt-6 mb-2">Preferences</h3>
             <dl className="divide-y divide-brand-border border-t border-brand-border">
                <InfoRow label="Subject Preference" value={user.subjectPreference} />
                <InfoRow label="Daily Study Goal" value={user.dailyStudyGoal} />
            </dl>
        </>
    );
    
    // Renders a form to edit the user's profile information.
    const renderEditMode = () => (
         <div className="space-y-6">
            <div>
                 <h3 className="text-lg font-semibold text-brand-text-primary mb-2">Personal Information</h3>
                 <div className="border-t border-brand-border space-y-4 pt-4">
                    <InputField label="Full Name" name="fullName" value={formData.fullName || ''} onChange={handleInputChange} />
                    <InputField label="Date of Birth" name="dob" value={formData.dob || ''} onChange={handleInputChange} type="date" />
                    <InputField label="Phone Number" name="phone" value={formData.phone || ''} onChange={handleInputChange} />
                    <InputField label="WhatsApp Number" name="whatsapp" value={formData.whatsapp || ''} onChange={handleInputChange} />
                    <InputField label="Address" name="address" value={formData.address || ''} onChange={handleInputChange} />
                 </div>
            </div>
            <div>
                 <h3 className="text-lg font-semibold text-brand-text-primary mb-2">Academic Information</h3>
                 <div className="border-t border-brand-border space-y-4 pt-4">
                    <InputField label="Target Exam" name="exam" value={formData.exam || ''} onChange={handleInputChange} />
                    <InputField label="Target Year" name="targetYear" value={formData.targetYear || ''} onChange={handleInputChange} />
                    <InputField label="Class" name="class" value={formData.class || ''} onChange={handleInputChange} />
                    <InputField label="School" name="school" value={formData.school || ''} onChange={handleInputChange} />
                    <InputField label="Board" name="board" value={formData.board || ''} onChange={handleInputChange} />
                    <InputField label="Medium" name="medium" value={formData.medium || ''} onChange={handleInputChange} />
                 </div>
            </div>
            <div>
                 <h3 className="text-lg font-semibold text-brand-text-primary mb-2">Preferences</h3>
                 <div className="border-t border-brand-border space-y-4 pt-4">
                    <SelectField label="Subject Preference" name="subjectPreference" value={formData.subjectPreference || ''} onChange={handleInputChange}>
                        <option value="">Select a subject</option>
                        <option value="Physics">Physics</option>
                        <option value="Chemistry">Chemistry</option>
                        <option value="Biology">Biology</option>
                        <option value="Maths">Maths</option>
                    </SelectField>
                    <SelectField label="Daily Study Goal" name="dailyStudyGoal" value={formData.dailyStudyGoal || ''} onChange={handleInputChange}>
                         <option value="">Select a goal</option>
                         <option value="30 mins">30 mins</option>
                         <option value="1 hr">1 hr</option>
                         <option value="2 hrs">2 hrs</option>
                         <option value="Flexible">Flexible</option>
                    </SelectField>
                 </div>
            </div>
         </div>
    );

    const stats = {
        testsCompleted: user.testsCompleted || 0,
        averageScore: user.averageScore || 0,
        totalStudyTime: user.totalStudyTime || 0,
        currentStreak: user.currentStreak || 0
    };

    return (
        <div className="animate-fade-in space-y-8">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-brand-primary to-brand-primary-hover rounded-xl p-6 text-white">
                <div className="flex items-center space-x-6">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                        {user.photoURL ? (
                            <img src={user.photoURL} alt="Profile" className="w-full h-full rounded-full object-cover" />
                        ) : (
                            <UserIcon className="w-10 h-10" />
                        )}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">{user.fullName || user.name || 'Student'}</h1>
                        <p className="text-white/80">{user.email}</p>
                        <p className="text-white/80">{user.exam || 'No target exam set'}</p>
                    </div>
                </div>
            </div>

            {/* Performance Lab: Personal Insights */}
            <div className="space-y-6">
                <div className="flex items-center space-x-2">
                    <ChartBarIcon className="w-6 h-6 text-brand-primary" />
                    <h2 className="text-xl font-bold text-brand-text-primary">Performance Lab: Personal Insights</h2>
                </div>

                {profileLoading ? (
                    <div className="flex justify-center p-12">
                        <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : performanceProfile ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Subject Mastery */}
                        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 shadow-card">
                            <h3 className="text-lg font-semibold mb-6 text-brand-text-primary">Subject Mastery (All-Time)</h3>
                            <div className="space-y-6">
                                {Object.entries(performanceProfile.subjects || {}).map(([subject, stats]: [string, any]) => (
                                    <div key={subject} className="space-y-2">
                                        <div className="flex justify-between items-end">
                                            <span className="font-semibold text-brand-text-primary">{subject}</span>
                                            <span className="text-xs font-bold text-brand-primary">{stats.accuracy.toFixed(0)}% Mastery</span>
                                        </div>
                                        <div className="h-3 w-full bg-brand-bg rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-1000 ${
                                                    stats.accuracy >= 80 ? 'bg-green-500' :
                                                    stats.accuracy >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${stats.accuracy}%` }}
                                            ></div>
                                        </div>
                                        <div className="flex justify-between text-[10px] text-brand-text-secondary uppercase tracking-widest font-medium">
                                            <span>Solved: {Math.round(stats.total || 0)}</span>
                                            <span>Speed: {(stats.avgTime || 0).toFixed(1)}s/q</span>
                                        </div>
                                    </div>
                                ))}
                                {Object.keys(performanceProfile.subjects || {}).length === 0 && (
                                    <div className="text-center py-8 text-brand-text-secondary italic">
                                        Take more tests to see your subject mastery breakdown.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Persistent Struggle Zones */}
                        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 shadow-card">
                            <h3 className="text-lg font-semibold mb-6 text-brand-text-primary text-red-500">Recurring Challenges</h3>
                            <div className="space-y-4">
                                {(performanceProfile.struggleZones || []).length > 0 ? (
                                    (performanceProfile.struggleZones || []).map((zone: any, index: number) => (
                                        <div key={index} className="p-4 bg-red-50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/20 rounded-xl">
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="font-bold text-sm text-red-700 dark:text-red-400">{zone.name}</h4>
                                                <span className="text-[10px] px-2 py-1 rounded bg-white dark:bg-zinc-800 border border-red-200 dark:border-red-800 text-red-600 font-bold uppercase">
                                                    {zone.accuracy < 40 ? 'Critical' : 'Weak Area'}
                                                </span>
                                            </div>
                                            <div className="flex items-center text-[10px] text-brand-text-secondary mb-3">
                                                <span>{zone.subject}</span>
                                                <span className="mx-2">•</span>
                                                <span>Accuracy: {zone.accuracy.toFixed(0)}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-brand-bg rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-red-500 rounded-full"
                                                    style={{ width: `${zone.accuracy}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <CheckCircleIcon className="w-12 h-12 text-green-500 mb-3 opacity-30" />
                                        <p className="text-sm text-brand-text-secondary">No persistent struggle zones! You're performing consistently well.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-brand-surface border border-brand-border rounded-xl p-12 text-center shadow-card">
                        <ChartBarIcon className="w-12 h-12 text-brand-primary/20 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-brand-text-primary mb-2">No Performance Data Yet</h3>
                        <p className="text-sm text-brand-text-secondary max-w-md mx-auto">
                            Complete your first mock test to unlock deep behavioral insights and subject mastery tracking in your profile.
                        </p>
                    </div>
                )}
            </div>

            {/* Profile Information */}
            <div className="bg-brand-surface border border-brand-border rounded-xl shadow-card">
                 <div className="p-6 border-b border-brand-border flex items-center justify-between">
                     <div>
                        <h2 className="text-xl font-bold text-brand-text-primary">Profile Information</h2>
                        <p className="mt-1 text-sm text-brand-text-secondary">
                           View and manage your personal and academic details.
                        </p>
                    </div>
                     {!isEditing && (
                        <button onClick={() => setIsEditing(true)} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-primary-hover transition-colors">
                            Edit Profile
                        </button>
                    )}
                 </div>
                 <div className="p-6">
                    {isEditing ? renderEditMode() : renderViewMode()}
                 </div>
                 {isEditing && (
                    <div className="p-6 bg-gray-50 dark:bg-brand-bg rounded-b-xl border-t border-brand-border flex justify-end gap-3">
                         <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border hover:bg-gray-100 dark:hover:bg-zinc-700">
                            Cancel
                        </button>
                        <button onClick={handleSaveChanges} disabled={loading} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-primary-hover disabled:opacity-50">
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                 )}
            </div>
        </div>
    );
};